import { createRouter, createWebHistory } from 'vue-router'
import DefaultLayout from '@/layout/DefaultLayout.vue'
import EventConfigLayout from '@/layout/EventConfigLayout.vue'
import Login from '@/views/pages/Login.vue'
import CompanyLogin from '@/views/pages/CompanyLogin.vue'
import ForgotPassword from '@/views/pages/ForgotPassword.vue'
import ResetPassword from '@/views/pages/ResetPassword.vue'
import Dashboard from '@/views/DashboardView.vue'
import { useEventStore } from '@/stores/eventStore'
import { useVisualConfigStore } from '@/stores/visualConfig'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: DefaultLayout,
      redirect: '/dashboard',
      children: [
        {
          path: '/user/settings',
          name: 'User Settings',
          component: () => import('@/views/Core/Account/UserSettingsView.vue'),
          beforeEnter: [requireAuth]
        },
        {
          path: '/account/settings',
          name: 'Account Settings',
          component: () => import('@/views/Core/Account/AccountSettingsView.vue'),
          beforeEnter: [requireAuth]
        },
        {
          path: '/dashboard',
          name: 'Coming events',
          component: Dashboard,
          beforeEnter: [requireAuth, restoreEventStore]
        },
        {
          path: '/events',
          name: 'Events',
          component: () => import('@/views/Core/Event/IndexEventView.vue'),
          beforeEnter: [requireAuth, restoreEventStore]
        },
        {
          path: '/users',
          name: 'Users',
          component: () => import('@/views/Core/User/UserView.vue'),
          beforeEnter: [requireAuth, restoreEventStore, authorize],
          meta: {
            policy: () => import('@/policies/UserPolicy')
          }
        },
        {
          path: '/users/create',
          name: 'Users / Create',
          component: () => import('@/views/Core/User/CreateUserView.vue'),
          beforeEnter: [requireAuth, restoreEventStore, authorize],
          meta: {
            policy: () => import('@/policies/UserPolicy')
          }
        },
        {
          path: '/users/edit/:id',
          name: 'Users / Edit',
          component: () => import('@/views/Core/User/EditUserView.vue'),
          beforeEnter: [requireAuth, restoreEventStore, authorize],
          meta: {
            policy: () => import('@/policies/UserPolicy')
          }
        },
        {
          path: '/customers',
          name: 'Customers',
          component: () => import('@/views/Core/Customer/CustomerView.vue'),
          beforeEnter: [requireAuth, requireSuperadmin, restoreEventStore],
          children: [
            {
              path: '/customers/:name/api-config/:id',
              name: 'Api configuration',
              component: () => import('@/views/Core/Customer/Apis/ApiConfig.vue'),
              beforeEnter: [requireAuth, requireSuperadmin]
            },
            {
              path: ':id',
              name: 'Customer Details',
              component: () => import('@/views/Core/Customer/CustomerDetails.vue'),
              beforeEnter: [requireAuth, requireSuperadmin],
              children: [
                {
                  path: 'licenses',
                  name: 'Licenses',
                  component: () => import('@/views/Core/License/LicenseView.vue'),
                  beforeEnter: [requireAuth, requireSuperadmin]
                }
              ]
            }
          ]
        },
        {
          path: '/stands',
          name: 'Stands',
          component: () => import('@/views/Core/Stand/StandView.vue'),
          beforeEnter: [requireAuth, requireSuperadmin],
          children: [
            {
              name: 'Stands configuration',
              path: 'configuration',
              beforeEnter: [requireAuth, requireSuperadmin],
              component: () => import('@/views/Core/Stand/Configuration.vue')
            },

            {
              name: 'Stands list',
              path: '',
              beforeEnter: [requireAuth, requireSuperadmin],
              component: () => import('@/views/Core/Stand/IndexStandsView.vue')
            },
            {
              name: 'Stands edit',
              path: 'edit/:id',
              beforeEnter: [requireAuth, requireSuperadmin],
              component: () => import('@/views/Core/Stand/EditStands.vue')
            },
            {
              name: 'Aggregates',
              path: 'aggregates',
              component: () => import('@/views/Core/Stand/Aggregates/AggregatesView.vue'),
              beforeEnter: [requireAuth, requireSuperadmin],
              children: [
                {
                  name: 'Aggregates list',
                  path: '',
                  beforeEnter: [requireAuth, requireSuperadmin],
                  component: () => import('@/views/Core/Stand/Aggregates/Aggregates.vue')
                },
                {
                  name: 'Aggregates  Edit',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth, requireSuperadmin],
                  component: () => import('@/views/Core/Stand/Aggregates/EditAggregates.vue')
                }
              ]
            },

            {
              name: 'Aggregates categories',
              path: 'aggregates-categories',
              component: () => import('@/views/Core/Stand/AggregatesCategories/AggregatesCategoriesView.vue'),
              beforeEnter: [requireAuth, requireSuperadmin],
              children: [
                {
                  name: 'Aggregates categories list',
                  path: '',
                  beforeEnter: [requireAuth, requireSuperadmin],
                  component: () => import('@/views/Core/Stand/AggregatesCategories/AggregatesCategories.vue')
                },
                {
                  name: 'Aggregates Categories Edit',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth, requireSuperadmin],
                  component: () => import('@/views/Core/Stand/AggregatesCategories/EditAggregatesCategories.vue')
                }
              ]
            },
            {
              name: 'Stands billing data',
              path: 'billing-data',
              component: () => import('@/views/Core/Stand/BillingData/BillingDataView.vue'),
              beforeEnter: [requireAuth, requireSuperadmin],
              children: [
                {
                  name: 'Stands billing data list',
                  path: '',
                  beforeEnter: [requireAuth, requireSuperadmin],
                  component: () => import('@/views/Core/Stand/BillingData/BillingData.vue')
                },
                {
                  name: 'Stands billing data edit',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth, requireSuperadmin],
                  component: () => import('@/views/Core/Stand/BillingData/EditBillingData.vue')
                }
              ]
            }
          ]
        }
      ]
    },

    {
      path: '/events',
      name: 'Event',
      beforeEnter: [retrieveEventInfo, retrieveEventRoleInfo, requireAuth],
      component: EventConfigLayout,
      children: [
        {
          name: ':event_id',
          path: ':event_id',
          beforeEnter: [requireAuth],
          component: () => import('@/views/Core/Event/EventDashboard.vue')
        },
        {
          name: ':event_id',
          path: ':event_id',
          children: [
            {
              name: 'Event stands',
              path: 'stands',
              component: () => import('@/views/Stands/StandView.vue'),
              beforeEnter: [requireAuth],
              children: [
                {
                  name: 'Stands event configuration',
                  path: 'configuration',
                  beforeEnter: [requireAuth, requireSuperadmin],
                  component: () => import('@/views/Stands/Configuration.vue')
                },
                {
                  name: 'Event stands list',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Stands/IndexStandsView.vue')
                },
                {
                  name: 'Edit event Stand',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Stands/EditStands.vue')
                }
              ]
            },

            {
              name: 'Push notifications',
              path: 'push-notifications',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Core/Communications/PushNotifications/PushNotificationsView.vue'),
              children: [
                {
                  name: 'Push notifications tabs',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/PushNotifications/PushNotificationsIndex.vue')
                },
                {
                  name: 'Edit channel',
                  path: 'editchannel/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/PushNotifications/EditChannel.vue')
                }
              ]
            },
            {
              name: 'Realtime',
              path: 'realtime',
              component: () => import('@/views/Core/Dashboard/RealtimeView.vue'),
              beforeEnter: [requireAuth]
            },
            {
              name: 'Config',
              path: 'config',
              component: () => import('@/views/Core/Event/EditEventView.vue'),
              beforeEnter: [requireAuth]
            },
            {
              name: 'E-mail',
              path: 'email',
              component: () => import('@/views/Core/Communications/CommunicationsView.vue'),
              beforeEnter: [requireAuth],
              children: [
                {
                  name: 'Campaigns',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Campaign/IndexEmailCampaignView.vue')
                },
                {
                  name: 'Campaigns / Create',
                  path: 'campaigns/create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Campaign/CreateEmailCampaignView.vue')
                },
                {
                  name: 'Campaigns / Edit',
                  path: 'campaigns/edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Campaign/EditEmailCampaignView.vue')
                },
                {
                  name: 'Templates',
                  path: 'templates',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Templates/IndexTemplatesView.vue')
                },
                {
                  name: 'Templates / Create',
                  path: 'templates/create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Templates/CreateTemplateView.vue')
                },
                {
                  name: 'Templates / Edit',
                  path: 'templates/edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Templates/EditTemplateView.vue')
                },
                {
                  name: 'Triggers',
                  path: 'triggers',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Triggers/IndexTriggersView.vue')
                },
                {
                  name: 'Default Templates',
                  path: 'templates-default',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Templates/IndexDefaultTemplatesView.vue')
                },
                {
                  name: 'Default Templates / Edit',
                  path: 'templates-default/view/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Communications/Email/Templates/EditDefaultTemplateView.vue')
                }
              ]
            },
            {
              name: 'Personas',
              path: 'inscriptions',
              component: () => import('@/views/Inscriptions/InscriptionsView.vue'),
              beforeEnter: [requireAuth],
              children: [
                {
                  name: 'Todas',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Personas/AllView.vue')
                },
                {
                  name: 'Advanced Search',
                  path: 'search',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Personas/AdvancedSearch/AdvancedSearchView.vue')
                },
                {
                  name: 'Create Persona',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Personas/CreatePersonaView.vue')
                },
                {
                  name: 'Edit Persona',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Personas/EditPersonaView.vue')
                },
                // {
                //   name: 'Assistants',
                //   path: 'assistants',
                //   beforeEnter: [requireAuth],
                //   component: () => import('@/views/Inscriptions/Personas/Assistants/AssistantsView.vue')
                // },
                // {
                //   name: 'Speakers',
                //   path: 'speakers',
                //   beforeEnter: [requireAuth],
                //   component: () => import('@/views/Inscriptions/Personas/Speakers/SpeakersView.vue')
                // },
                // {
                //   name: 'Create Speaker',
                //   path: 'speakers/create',
                //   beforeEnter: [requireAuth],
                //   component: () => import('@/views/Inscriptions/Personas/CreatePersonaView.vue')
                // },
                // {
                //   name: 'Sponsors',
                //   path: 'sponsors',
                //   beforeEnter: [requireAuth],
                //   component: () => import('@/views/Inscriptions/Companies/Sponsors/SponsorsView.vue')
                // },
                {
                  name: 'Exhibitors',
                  path: 'exhibitors',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Companies/Exhibitors/ExhibitorsView.vue')
                },
                {
                  name: 'Forms',
                  path: 'setup',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Personas/Setup/IndexPersonasSetupView.vue'),
                  children: [
                    {
                      name: 'Edit Form',
                      path: ':id',
                      beforeEnter: [requireAuth],
                      component: () => import('@/views/Inscriptions/Personas/Setup/EditPersonasSetupView.vue')
                    },
                    {
                      name: 'Imported Records / List',
                      path: 'imported-records',
                      beforeEnter: [requireAuth],
                      component: () => import('@/views/Event/Persona/Setup/ImportedRecord/IndexImportedRecordView.vue')
                    }
                  ]
                },
                {
                  name: 'Imported Records / List',
                  path: 'setup/imported-records',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Event/Persona/Setup/ImportedRecord/IndexImportedRecordView.vue')
                },
                {
                  name: 'Imported Records / Detail',
                  path: 'setup/imported-records/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Event/Persona/Setup/ImportedRecord/ShowImportedRecordView.vue')
                }
              ]
            },
            {
              name: 'Activities',
              path: 'activities',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Activities/ActivitiesView.vue'),
              children: [
                {
                  name: 'List of Activities',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Activities/Activities/IndexActivitiesView.vue')
                },
                {
                  name: 'Inventory of Activities',
                  path: 'inventory',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Activities/Activities/IndexActivitiesInventoryView.vue')
                },
                {
                  name: 'Create Activity',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Activities/Activities/CreateActivityView.vue')
                },
                {
                  name: 'Edit Activity',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Activities/Activities/EditActivityView.vue')
                },
                {
                  name: 'Categories',
                  path: 'activitytypes',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Activities/ActivityTypes/ActivityTypesView.vue'),
                  children: [
                    {
                      name: 'List Categories',
                      path: '',
                      beforeEnter: [requireAuth],
                      component: () => import('@/views/Activities/ActivityTypes/IndexActivityTypesView.vue')
                    },
                    {
                      name: 'Edit Category',
                      path: 'edit/:id',
                      beforeEnter: [requireAuth],
                      component: () => import('@/views/Activities/ActivityTypes/EditActivityTypesView.vue')
                    }
                  ]
                },
                {
                  name: 'Subcategories',
                  path: 'activitytypecategories',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Activities/ActivityTypeCategories/ActivityTypeCategoriesView.vue'),
                  children: [
                    {
                      name: 'List of Subcategories',
                      path: '',
                      beforeEnter: [requireAuth],
                      component: () =>
                        import('@/views/Activities/ActivityTypeCategories/IndexActivityTypeCategoriesView.vue')
                    },
                    {
                      name: 'Create Subcategory',
                      path: 'create',
                      beforeEnter: [requireAuth],
                      component: () =>
                        import('@/views/Activities/ActivityTypeCategories/CreateActivityTypeCategoriesView.vue')
                    },
                    {
                      name: 'Edit Subcategory',
                      path: 'edit/:id',
                      beforeEnter: [requireAuth],
                      component: () =>
                        import('@/views/Activities/ActivityTypeCategories/EditActivityTypeCategoriesView.vue')
                    }
                  ]
                }
              ]
            },
            {
              name: 'Tickets',
              path: 'tickets',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Tickets/TicketsView.vue'),
              children: [
                {
                  name: 'Tickets list',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Tickets/IndexTicketsView.vue')
                },
                {
                  name: 'Inventory',
                  path: 'inventory',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Tickets/IndexTicketsInventoryView.vue')
                },
                {
                  name: 'Create ticket',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Tickets/CreateTicketView.vue')
                },
                {
                  name: 'Edit ticket',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Tickets/EditTicketView.vue')
                }
              ]
            },
            {
              name: 'Accounting',
              path: 'accounting',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Accounting/AccountingView.vue'),
              children: [
                {
                  name: 'Configuration',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Configuration/AccountingConfigurationView.vue')
                },
                {
                  name: 'Purchases',
                  path: 'purchases',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Purchases/IndexAccountingPurchasesView.vue')
                },
                {
                  name: 'Advanced Purchases search',
                  path: 'search',
                  beforeEnter: [requireAuth],
                  component: () =>
                    import('@/views/Accounting/Purchases/AdvancedPurchasesSearch/AdvancedPurchasesSearch.vue')
                },
                {
                  name: 'Create purchase',
                  path: 'purchases/create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Purchases/CreateAccountingPurchases.vue')
                },
                {
                  name: 'Edit purchase',
                  path: 'purchases/edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Purchases/EditAccountingPurchaseView.vue')
                },
                {
                  name: 'Deleted Purchases',
                  path: 'purchases/deleted',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Purchases/DeletedAccountingPurchaseView.vue')
                },
                {
                  name: 'Payments',
                  path: 'payments',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Payments/IndexAccountingPaymentsView.vue')
                },
                {
                  name: 'Invoices',
                  path: 'invoices',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Invoices/IndexAccountingInvoicesView.vue')
                },
                {
                  name: 'Receipts',
                  path: 'receipts',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Accounting/Receipts/IndexAccountingReceiptsView.vue')
                }
              ]
            },
            {
              name: 'Certificates',
              path: 'certificates',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Inscriptions/Certificates/CertificateView.vue'),
              children: [
                {
                  name: 'List of certificates',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Certificates/IndexCertificatesView.vue')
                },
                {
                  name: 'Create certificate',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Certificates/CreateCertificateView.vue')
                },
                {
                  name: 'Edit certificate',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Certificates/EditCertificateView.vue')
                }
              ]
            },

            {
              name: 'Badges',
              path: 'badges',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Inscriptions/Badges/BadgeView.vue'),
              children: [
                {
                  name: 'List of badges',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Badges/IndexBadgesView.vue')
                },
                {
                  name: 'Create badge',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Badges/CreateBadgeView.vue')
                },
                {
                  name: 'Edit badge',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Badges/EditBadgeView.vue')
                }
              ]
            },

            {
              name: 'Hotels',
              path: 'hotels',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Inscriptions/Hotels/HotelsView.vue'),
              children: [
                {
                  name: 'List',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Hotels/IndexHotelsView.vue')
                },
                {
                  name: 'Create',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Hotels/CreateHotelsView.vue')
                },
                {
                  name: 'Edit',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Hotels/EditHotelsView.vue')
                }
              ]
            },
            {
              name: 'Rooms',
              path: 'rooms',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Core/ConferenceRoom/ConferenceRoomView.vue'),
              children: [
                {
                  name: 'List of Conference Rooms',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/ConferenceRoom/IndexConferenceRoomView.vue')
                },
                {
                  name: 'Create Conference Room',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/ConferenceRoom/CreateConferenceRoomView.vue')
                },
                {
                  name: 'Edit Conference Room',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/ConferenceRoom/EditConferenceRoomView.vue')
                }
              ]
            },
            {
              name: 'Topics',
              path: 'topics',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Core/Topic/TopicView.vue'),
              children: [
                {
                  name: 'List of topics',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Topic/IndexTopic.vue')
                },
                {
                  name: 'Create topic',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Topic/CreateTopic.vue')
                },
                {
                  name: 'Edit topic',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/Topic/EditTopic.vue')
                }
              ]
            },

            {
              name: 'Sessions',
              path: 'sessions',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Core/Session/SessionView.vue')
            },
            {
              name: 'Presentations',
              path: 'presentations',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Core/Presentation/PresentationView.vue')
            },
            {
              name: 'Schedules',
              path: 'schedules',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Core/Schedule/ScheduleView.vue'),
              children: [
                {
                  name: 'List of Schedules',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/ConferenceRoom/IndexConferenceRoomView.vue')
                }
              ]
            },

            {
              name: 'Groups',
              path: 'groups',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Core/PersonaGroup/PersonaGroupsView.vue'),
              children: [
                {
                  name: 'List of Persona Groups',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/PersonaGroup/IndexPersonaGroupsView.vue')
                },
                {
                  name: 'Create Persona Group',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/PersonaGroup/CreatePersonaGroupView.vue')
                },
                {
                  name: 'Edit Persona Group',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Core/PersonaGroup/EditPersonaGroupView.vue')
                }
              ]
            },

            {
              name: 'Companies',
              path: 'companies',
              beforeEnter: [requireAuth],
              component: () => import('@/views/Inscriptions/Companies/CompaniesView.vue'),
              children: [
                {
                  name: 'List of Companies',
                  path: '',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Companies/IndexCompaniesView.vue')
                },
                {
                  name: 'General configuration of Companies',
                  path: 'configuration',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Companies/CompaniesConfigurationView.vue')
                },
                {
                  name: 'Create company',
                  path: 'create',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Companies/CreateCompanyView.vue')
                },
                {
                  name: 'Edit company',
                  path: 'edit/:id',
                  beforeEnter: [requireAuth],
                  component: () => import('@/views/Inscriptions/Companies/EditCompanyView.vue')
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      beforeEnter: [requireCSRFToken, isLogged]
    },
    {
      path: '/companies/login',
      name: 'Company login',
      component: CompanyLogin,
      beforeEnter: [requireCSRFToken, isLogged]
    },
    {
      path: '/forgot-password',
      name: 'Forgot Password',
      component: ForgotPassword,
      beforeEnter: [requireCSRFToken, isLogged]
    },
    {
      path: '/reset-password',
      name: 'Reset Password',
      component: ResetPassword,
      beforeEnter: [requireCSRFToken, isLogged]
    },
    {
      path: '/about',
      name: 'about',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/AboutView.vue')
    }
  ]
})

router.beforeEach(async (to, from) => {
  const { userAuthCheck } = useUserStore()
  await userAuthCheck()

  // Ocultar sidebar en pantallas pequeñas al navegar
  if (window.innerWidth <= 768) {
    const visualConfigStore = useVisualConfigStore()
    visualConfigStore.sidebarVisible = false
  }
})

function requireAuth(to, from, next) {
  const { auth } = storeToRefs(useUserStore())
  if (unref(auth) === 'Unauthenticated') {
    next('/login')
  } else if (unref(auth) === 'Authenticated') {
    next()
  }
}

async function retrieveEventInfo(to, from, next) {
  const eventStore = useEventStore()
  await eventStore.retrieve(to.params.event_id)
  next()
}

async function retrieveEventRoleInfo(to, from, next) {
  const eventStore = useEventStore()
  await eventStore.retrieveRoles(to.params.event_id)
  next()
}

async function restoreEventStore(to, from, next) {
  const eventStore = useEventStore()
  eventStore.$reset()
  next()
}

function requireCSRFToken() {
  // const { get } = useCookies(['XSRF-TOKEN'])
  // useFetch('/api/v1/csrf-token')
}

function isLogged(to, from, next) {
  const { auth, user } = storeToRefs(useUserStore())
  if (unref(auth) === 'Authenticated') {
    console.log(user)
    if (user.value.company_id && user.value.event_id) {
      next(`/events/${user.value.event_id}/companies/edit/${user.value.company_id}`)
    } else {
      next('/dashboard')
    }
    return
  }
  next()
}

// router.onError((error, to) => {
//   if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes("Importing a module script failed")) {
//     window.location = to.fullPath
//   }
// })

function requireSuperadmin(to, from, next) {
  const { roles } = storeToRefs(useUserStore())
  if (unref(roles).includes('superadmin'.toLowerCase())) {
    next()
    return
  }
  next(from)
}

async function authorize(to, from, next) {
  const policy = await to.meta.policy()
  const userStore = useUserStore()
  policy.default(to, from, next, userStore)
}

export default router
